import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Paper, 
  Alert, 
  Button,
  Container
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon, 
  Error as ErrorIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { getApiBaseUrl } from '../../utils/apiConfig';

const API_BASE_URL = getApiBaseUrl();

const TeamInvitationAccept: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresRegistration, setRequiresRegistration] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState<string | null>(null);

  useEffect(() => {
    const handleAccept = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setError('Invalid invitation link. Missing token.');
        setLoading(false);
        return;
      }

      try {
        // Call backend API to accept the invitation
        const response = await axios.get(
          `${API_BASE_URL}/api/team/invitation/accept`,
          {
            params: { token },
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          if (response.data.requiresRegistration) {
            setRequiresRegistration(true);
            setInvitationEmail(response.data.email);
            setSuccess(false);
          } else {
            setSuccess(true);
            // Redirect to dashboard after 3 seconds
            setTimeout(() => {
              navigate('/dashboard');
            }, 3000);
          }
        } else {
          setError(response.data.message || 'Failed to accept invitation');
        }
      } catch (err: any) {
        console.error('Error accepting invitation:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to accept invitation';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    handleAccept();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <Container maxWidth="sm" style={{ marginTop: '100px' }}>
        <Paper style={{ padding: '40px', textAlign: 'center' }}>
          <CircularProgress style={{ marginBottom: '20px' }} />
          <Typography variant="h6">Processing your invitation...</Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" style={{ marginTop: '100px' }}>
        <Paper style={{ padding: '40px', textAlign: 'center' }}>
          <ErrorIcon color="error" style={{ fontSize: 64, marginBottom: '20px' }} />
          <Typography variant="h5" gutterBottom style={{ color: '#d32f2f' }}>
            Invitation Error
          </Typography>
          <Alert severity="error" style={{ marginTop: '20px', marginBottom: '20px' }}>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate('/')}
            style={{ marginTop: '20px' }}
          >
            Go to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  if (requiresRegistration) {
    return (
      <Container maxWidth="sm" style={{ marginTop: '100px' }}>
        <Paper style={{ padding: '40px', textAlign: 'center' }}>
          <LoginIcon color="primary" style={{ fontSize: 64, marginBottom: '20px' }} />
          <Typography variant="h5" gutterBottom>
            Create Account to Accept Invitation
          </Typography>
          <Alert severity="info" style={{ marginTop: '20px', marginBottom: '20px' }}>
            You need to create an account first to accept this team invitation.
            <br />
            <strong>Please use this email: {invitationEmail}</strong>
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate(`/register?email=${encodeURIComponent(invitationEmail || '')}`)}
            style={{ marginTop: '20px', marginRight: '10px' }}
          >
            Create Account
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
            style={{ marginTop: '20px' }}
          >
            Go to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" style={{ marginTop: '100px' }}>
      <Paper style={{ padding: '40px', textAlign: 'center' }}>
        <CheckCircleIcon color="success" style={{ fontSize: 64, marginBottom: '20px' }} />
        <Typography variant="h5" gutterBottom style={{ color: '#2e7d32' }}>
          Invitation Accepted!
        </Typography>
        <Alert severity="success" style={{ marginTop: '20px', marginBottom: '20px' }}>
          You have successfully accepted the team invitation. You are now a team member!
        </Alert>
        <Typography variant="body2" color="text.secondary" style={{ marginTop: '20px' }}>
          Redirecting to dashboard...
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/dashboard')}
          style={{ marginTop: '20px' }}
        >
          Go to Dashboard
        </Button>
      </Paper>
    </Container>
  );
};

export default TeamInvitationAccept;

